// @ts-strict-ignore
/** @jsx jsx */
/** @jsxFrag null */
import {jsx} from 'panel';

import {LocalizationStore} from 'common/i18n/store';
import {MarkdownEl} from 'common/components/markdown';
import {ButtonEl} from 'common/components/button';
import {ButtonSize, ButtonTheme} from 'common/components/button/constants';
import {ButtonBarEl} from 'common/components/button-bar';
import {ButtonBarTheme} from 'common/components/button-bar/types';
import {DropMenuEl} from 'common/components/drop-menu';
import {Directions} from 'common/components/drop-menu/types';
import {InputEl} from 'common/components/input';
import {InputGroupEl} from 'common/components/input-group';
import {SelectTheme} from 'common/components/select/constants';
import {SelectEl} from 'common/components/select';
import {SpinnerEl} from 'common/components/spinner';
import {TooltipEl} from 'common/components/tooltip';
import {Placement} from 'common/components/tooltip/types';
import {ProjectSelectedEvent, TargetProjectSelectorMenuEl} from 'common/widgets/target-project-selector-menu';
import {ListItemEl} from 'common/components/list-item';

import type {DashboardSelector} from './index';

const ProjectSelectorMenu = ({
  selectedProjectName,
  onProjectSelected,
  i18n,
}: {
  selectedProjectName: string;
  onProjectSelected: (ev: ProjectSelectedEvent) => void;
  i18n: LocalizationStore;
}) => {
  return (
    <div sel='.mp-dashboard-selector-target-project-selector-container'>
      <span sel='.mp-dashboard-selector-project-selector-menu-label'>{i18n.t(`boards.selector.project`)}</span>
      <SelectEl
        sel='.mp-dashboard-selector-project-selection'
        attrs={{
          'force-visibility': true,
          'menu-alignment': Directions.DownCenter,
          'selected-option': selectedProjectName || ``,
          size: ButtonSize.Inline,
          theme: SelectTheme.MenuFilter,
        }}
      >
        <div attrs={{slot: `body`}}>
          <TargetProjectSelectorMenuEl
            params={{
              isFeatureGateEnterpriseTier: true,
            }}
            on={{
              projectSelected: onProjectSelected,
            }}
          />
        </div>
      </SelectEl>
    </div>
  );
};

/**
 * Sometimes you can move a dashboard without selecting another dashboard. ("Move out of all boards" or "Move to your boards" when selecting another project)
 */
const MoveActionButton = ({
  onMoveBoard,
  isCurrentProjectSelected,
  isCrossProjectMoveEnabled,
  isNestedDashboardsEnabled,
  showMoveOutButton,
  i18n,
}: {
  onMoveBoard: (ev) => void;
  isCurrentProjectSelected: boolean;
  isCrossProjectMoveEnabled: boolean;
  isNestedDashboardsEnabled: boolean;
  showMoveOutButton: boolean;
  i18n: LocalizationStore;
}) => {
  let message = null;
  let disabled = false;
  let tooltipMessage = null;

  if (isCurrentProjectSelected && (isNestedDashboardsEnabled || showMoveOutButton)) {
    if (!showMoveOutButton) {
      return null;
    }
    message = i18n.t(`boards.selector.move-out-all`);
  } else if (isCrossProjectMoveEnabled) {
    message = <MarkdownEl attrs={{text: i18n.t(`boards.selector.move-to-your-boards`)}} />;
    disabled = isCurrentProjectSelected;
    tooltipMessage = isCurrentProjectSelected ? i18n.t(`boards.selector.no-move-access`) : null;
  }

  if (!message) {
    return null;
  }

  return (
    <ListItemEl
      attrs={{'style-override': `.mp-list-item-container {padding:0}`, disabled}}
      on={{click: disabled ? null : onMoveBoard}}
    >
      <div sel='.mp-dashboard-selector-move-target-label' class={{'mp-dashboard-selector-disabled': disabled}}>
        {message}
      </div>
      {tooltipMessage && (
        <TooltipEl attrs={{placement: Placement.Top, 'max-width': `300px`}}>{tooltipMessage}</TooltipEl>
      )}
    </ListItemEl>
  );
};

const SearchableText = ({
  value,
  placeholder,
  searchTokens,
}: {
  value: string;
  placeholder: string;
  searchTokens: string[];
}) =>
  !value ? (
    <i>{placeholder}</i>
  ) : searchTokens ? (
    <>{searchTokens.map((token, pos) => (pos % 2 === 1 ? token : <b>{token}</b>))}</>
  ) : (
    <>{value}</>
  );

export function template(this: DashboardSelector) {
  const {activeItem, busy, controller, creating, disableCreateDashboard, newTitle, open, searchFilter, sections} =
    this.state;

  const disabledTooltip = (item) => {
    const dashboardDisabledStatus = this.helpers.getDashboardDisabledStatus(item);

    const message = this.helpers.getMessageForDisabledStatus(dashboardDisabledStatus);

    if (message) {
      return <TooltipEl attrs={{placement: Placement.Top}}>{message}</TooltipEl>;
    }
    return null;
  };

  const isMovingDashboardToProjectWithoutFF =
    this.helpers.isNestedBoardsEnabled() &&
    this.attr(`enable-nested-dashboards-restrictions`) &&
    !this.state.isNestedBoardsEnabledForTargetProject;

  return (
    <DropMenuEl
      attrs={{
        open,
        direction: this.attr(`direction`),
        offset: this.attr(`offset`),
        ...(this.attr(`position-strategy`) ? {'position-strategy': this.attr(`position-strategy`)} : {}),
      }}
      props={{
        positionParent: this,
      }}
      on={{change: this.helpers.handleMenuChange, perfpoint: this.helpers.handlePerfPoint}}
    >
      <div sel='.mp-dashboard-selector-container'>
        {/* used for linked-copy move board. extra inline-tooltip explaining what create a link and move to other board does */}
        {this.params.detailedExplainCopy && (
          <>
            <div sel='.mp-dashboard-selector-detailed-explain-copy-title'>
              {this.print(this.params.detailedExplainCopy.title)}
            </div>
            <div sel='.mp-dashboard-selector-detailed-explain-copy-description'>
              {this.print(this.params.detailedExplainCopy.description)}
            </div>
          </>
        )}
        {!creating ? (
          <>
            {this.attr(`hide-dashboard-list`) ? null : (
              <div sel='.mp-dashboard-selector-search-container'>
                <InputEl
                  attrs={{
                    icon: `search`,
                    placeholder: this.helpers.getSearchPlaceholder(),
                  }}
                  on={{input: this.helpers.handleSearchInput}}
                  props={{value: searchFilter}}
                  hook={{insert: this.helpers.handleSearchInputInsertHook}}
                />
              </div>
            )}
            {this.helpers.isCrossProjectEnabled() && (
              <ProjectSelectorMenu
                selectedProjectName={this.helpers.getMoveToTargetProject()?.name}
                onProjectSelected={this.helpers.updateDashboardSelectorMenu}
                i18n={this.getContext(`i18n`)}
              />
            )}
            <MoveActionButton
              isCurrentProjectSelected={
                this.state.moveToTargetProject?.id.toString() === this.getContext(`currentProject`).id
              }
              isCrossProjectMoveEnabled={this.helpers.isCrossProjectEnabled()}
              isNestedDashboardsEnabled={this.helpers.isNestedBoardsEnabled()}
              showMoveOutButton={this.attr(`show-move-out-button`)}
              onMoveBoard={this.helpers.moveBoard}
              i18n={this.getContext(`i18n`)}
            />
            {this.attr(`hide-dashboard-list`) ? null : !controller?.getLoaded() ? (
              <div
                sel='.mp-dashboard-selector-loading'
                class={{'mp-dashboard-selector-compact-mode': this.attr(`compact-mode`)}}
              >
                <SpinnerEl
                  attrs={{
                    elref: `dashboardSelectorLoadingSpinner`,
                  }}
                />
              </div>
            ) : (
              <>
                <div
                  sel='.mp-dashboard-selector-items-container'
                  class={{'mp-dashboard-selector-compact-mode': this.attr(`compact-mode`)}}
                >
                  {sections.length === 0 || (sections.length === 1 && sections[0].items.length === 0) ? (
                    <div sel='.mp-dashboard-selector-empty-state'>
                      {searchFilter ? this.t(`boards.selector.no-boards-found`) : this.t(`boards.selector.no-boards`)}
                    </div>
                  ) : isMovingDashboardToProjectWithoutFF ? (
                    <div sel='.mp-dashboard-selector-empty-state'>{this.t(`boards.selector.nested-disabled`)}</div>
                  ) : (
                    sections.map((section, sectionIndex) => (
                      <>
                        {sectionIndex !== 0 ? <div sel='.mp-dashboard-selector-divider' /> : null}
                        <section>
                          {section.title && <div sel='.mp-dashboard-selector-section-header'>{section.title}</div>}
                          {section.items.map((item) => (
                            <div sel='.mp-dashboard-selector-item-row-wrapper'>
                              {disabledTooltip(item)}
                              <div
                                sel='.mp-dashboard-selector-item-row'
                                key={item.dashboard.id}
                                class={{
                                  'mp-dashboard-selector-active':
                                    activeItem && activeItem.dashboard.id === item.dashboard.id,
                                  'mp-dashboard-selector-disabled':
                                    this.helpers.getDashboardDisabledStatus(item) !== null,
                                }}
                                on={{
                                  click: this.helpers.getItemActionHandler(item),
                                  mouseover: this.helpers.getItemMouseOverHandler(item),
                                }}
                                hook={{insert: this.helpers.handleItemRowInsertHook}}
                              >
                                <div sel='.mp-dashboard-selector-text'>
                                  <div
                                    sel='.mp-dashboard-selector-title'
                                    attrs={{title: item.dashboard.title}}
                                    class={{'mp-dashboard-selector-placeholder': !item.dashboard.title}}
                                  >
                                    <SearchableText
                                      value={item.dashboard.title}
                                      placeholder='Untitled board'
                                      searchTokens={item.titleSearchTokens}
                                    />
                                  </div>
                                  <div
                                    sel='.mp-dashboard-selector-sublabel'
                                    class={{
                                      'mp-dashboard-selector-placeholder': this.getDashboardSublabel(item.dashboard),
                                    }}
                                  >
                                    <SearchableText
                                      value={this.getDashboardSublabel(item.dashboard)}
                                      placeholder='No description'
                                      searchTokens={item.sublabelSearchTokens}
                                    />
                                  </div>
                                </div>
                              </div>
                            </div>
                          ))}
                        </section>
                      </>
                    ))
                  )}
                </div>
                {busy ? (
                  <div sel='.mp-dashboard-selector-busy'>
                    <SpinnerEl />
                  </div>
                ) : creating ? (
                  <div sel='.mp-dashboard-selector-create-field'>
                    <InputGroupEl
                      attrs={{placeholder: `New board title`, busy}}
                      on={{
                        input: this.helpers.handleCreateInput,
                        submit: this.helpers.handleCreateSubmit,
                      }}
                      props={{value: newTitle}}
                      hook={{insert: this.helpers.handleCreateInputInsertHook}}
                    />
                  </div>
                ) : this.attr(`quick-create`) ? (
                  <ButtonBarEl
                    attrs={{theme: ButtonBarTheme.Secondary}}
                    on={{click: this.helpers.handleQuickCreateClick}}
                  >
                    {this.helpers.getQuickCreateSaveText()}
                  </ButtonBarEl>
                ) : !disableCreateDashboard && !isMovingDashboardToProjectWithoutFF ? (
                  <ButtonBarEl attrs={{theme: ButtonBarTheme.Secondary}} on={{click: this.helpers.handleCreateClick}}>
                    {this.t(`boards.selector.create-new`)}
                  </ButtonBarEl>
                ) : this.attr(`enable-management`) ? (
                  <ButtonBarEl attrs={{theme: ButtonBarTheme.Secondary}} on={{click: this.helpers.handleManageClick}}>
                    {this.t(`boards.selector.manage`)}
                  </ButtonBarEl>
                ) : null}
              </>
            )}
          </>
        ) : (
          <div sel='.mp-dashboard-selector-new-dashboard-input'>
            <InputEl
              attrs={{
                placeholder: this.helpers.getSearchPlaceholder(),
              }}
              props={{value: this.t(`boards.header.title-placeholder`)}}
              on={{input: this.helpers.handleCreateInput}}
              hook={{insert: this.helpers.handleCreateInputInsertHook}}
            />
            <ButtonEl
              attrs={{
                theme: ButtonTheme.Primary,
                disabled: !newTitle,
              }}
              on={{
                click: this.helpers.handleCreateSubmit,
              }}
            >
              {this.t(`common.save`)}
            </ButtonEl>
          </div>
        )}
      </div>
    </DropMenuEl>
  );
}
